import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, MessageSquare, ArrowRight } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { formatPhone } from "../me-ui";

// "My Work Today" reconciled to docs/design/me-mockup.html (functional-first, D20):
// a KPI strip, the queue tabs (Call / Follow-ups / Mail / Reactivation), and rich
// lead rows (temperature, best-scored number, why-it's-due). Queues are queries;
// read/compute-only (D17).

type Tab = "call" | "followup" | "mail" | "react";

type Row = {
  lead_id: string;
  contact_name: string | null;
  address_line1: string | null;
  city: string | null;
  county: string | null;
  temperature: string | null;
  next_action_at: string | null;
  status?: string;
  call_attempts?: number;
  best_activity_score?: number | null;
  best_number?: string | null;
  // mail
  piece_to_send?: string;
  mail_step?: number;
  last_mailed_at?: string | null;
  // reactivation
  reactivation_at?: string | null;
  case_type?: string | null;
  best_number_react?: string | null;
};

const tempDot: Record<string, string> = { hot: "bg-red-500", warm: "bg-amber-500", cold: "bg-sky-500" };
const scoreClass = (s: number | null | undefined) =>
  s == null ? "bg-muted text-muted-foreground" : s >= 70 ? "bg-green-100 text-green-800" : s >= 45 ? "bg-amber-100 text-amber-800" : "bg-muted text-muted-foreground";

const Kpi = ({ label, value, accent }: { label: string; value: React.ReactNode; accent?: boolean }) => (
  <Card className={accent ? "border-l-4 border-l-amber-500" : ""}>
    <CardContent className="p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </CardContent>
  </Card>
);

const CallRow = ({ r }: { r: Row }) => (
  <Link to={`/leads/${r.lead_id}/show`} className="block">
    <Card className="mb-2 hover:border-amber-400 transition-colors">
      <CardContent className="p-3 flex items-center gap-3">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${tempDot[r.temperature ?? ""] ?? "bg-muted"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{r.contact_name || "(no name)"}</div>
          <div className="text-xs text-muted-foreground truncate">{[r.address_line1, r.city, r.county].filter(Boolean).join(", ") || "—"}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-mono flex items-center gap-1 justify-end">
            {formatPhone(r.best_number)}
            <span className={`text-[0.65rem] font-bold px-1.5 rounded ${scoreClass(r.best_activity_score)}`}>{r.best_activity_score ?? "—"}</span>
          </div>
          <div className="text-xs text-muted-foreground">{r.call_attempts ?? 0} tries · {r.status?.replace("_", " ")}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </CardContent>
    </Card>
  </Link>
);

const FollowRow = ({ r }: { r: Row }) => (
  <Link to={`/leads/${r.lead_id}/show`} className="block">
    <Card className="mb-2 hover:border-amber-400 transition-colors">
      <CardContent className="p-3 flex items-center gap-3">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${tempDot[r.temperature ?? ""] ?? "bg-muted"}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{r.contact_name || "(no name)"}</div>
          <div className="text-xs text-muted-foreground truncate">{[r.address_line1, r.city].filter(Boolean).join(", ") || "—"}</div>
        </div>
        {r.temperature && <Badge className={`capitalize ${r.temperature === "hot" ? "bg-red-100 text-red-800" : r.temperature === "warm" ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`}>{r.temperature}</Badge>}
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </CardContent>
    </Card>
  </Link>
);

const MailRow = ({ r, onMailed }: { r: Row; onMailed: (id: string) => void }) => (
  <Card className="mb-2">
    <CardContent className="p-3 flex items-center gap-3">
      <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-sky-500" />
      <Link to={`/leads/${r.lead_id}/show`} className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">{r.contact_name || "(no name)"}</div>
        <div className="text-xs text-muted-foreground truncate">{[r.address_line1, r.city].filter(Boolean).join(", ") || "—"}</div>
      </Link>
      <div className="text-right text-xs shrink-0">
        <div className="font-medium capitalize">{r.piece_to_send?.replace(/_/g, " ")}</div>
        <div className="text-muted-foreground">step {(r.mail_step ?? 0) + 1}{r.last_mailed_at ? ` · last ${new Date(r.last_mailed_at).toLocaleDateString()}` : " · never mailed"}</div>
      </div>
      <Button size="sm" onClick={() => onMailed(r.lead_id)}>Mark mailed</Button>
    </CardContent>
  </Card>
);

const ReactRow = ({ r }: { r: Row }) => (
  <Link to={`/leads/${r.lead_id}/show`} className="block">
    <Card className="mb-2 hover:border-amber-400 transition-colors">
      <CardContent className="p-3 flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full shrink-0 bg-slate-400" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{r.contact_name || "(no name)"}</div>
          <div className="text-xs text-muted-foreground truncate">{[r.address_line1, r.city].filter(Boolean).join(", ") || "—"}</div>
        </div>
        <div className="text-right text-xs shrink-0">
          {r.case_type && <Badge variant="outline" className="capitalize">{r.case_type}</Badge>}
          <div className="text-muted-foreground mt-0.5 capitalize">{r.status?.replace("_", " ")} · due {r.reactivation_at ? new Date(r.reactivation_at).toLocaleDateString() : ""}</div>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </CardContent>
    </Card>
  </Link>
);

const MyWorkPage = () => {
  const [tab, setTab] = useState<Tab>("call");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<Record<Tab, number>>({ call: 0, followup: 0, mail: 0, react: 0 });
  const [drafts, setDrafts] = useState(0);

  const viewFor = (t: Tab) => (t === "call" ? "me_call_queue" : t === "followup" ? "me_followup_queue" : t === "mail" ? "me_mail_queue" : "me_reactivation_queue");

  const loadCounts = async () => {
    const sb = getSupabaseClient();
    const [c, f, m, r, d] = await Promise.all([
      sb.from("me_call_queue").select("lead_id", { count: "exact", head: true }),
      sb.from("me_followup_queue").select("lead_id", { count: "exact", head: true }),
      sb.from("me_mail_queue").select("lead_id", { count: "exact", head: true }),
      sb.from("me_reactivation_queue").select("lead_id", { count: "exact", head: true }),
      sb.from("drafts").select("id", { count: "exact", head: true }).eq("status", "pending"),
    ]);
    setCounts({ call: c.count ?? 0, followup: f.count ?? 0, mail: m.count ?? 0, react: r.count ?? 0 });
    setDrafts(d.count ?? 0);
  };

  const loadRows = (t: Tab) => {
    const view = viewFor(t);
    if (!view) { setRows([]); setLoading(false); return; }
    setLoading(true);
    getSupabaseClient().from(view).select("*").then(({ data }) => { setRows((data as Row[]) ?? []); setLoading(false); });
  };

  useEffect(() => { loadCounts(); }, []);
  useEffect(() => { loadRows(tab); }, [tab]);

  const markMailed = async (leadId: string) => {
    await getSupabaseClient().rpc("mark_mailed", { p_lead_id: leadId });
    loadRows("mail"); loadCounts();
  };

  const tabs: { k: Tab; label: string; icon?: React.ReactNode }[] = [
    { k: "call", label: "Call queue", icon: <Phone className="h-3.5 w-3.5" /> },
    { k: "followup", label: "Follow-ups due" },
    { k: "mail", label: "Mail due", icon: <MessageSquare className="h-3.5 w-3.5" /> },
    { k: "react", label: "Reactivation" },
  ];

  return (
    <div className="p-4 max-w-4xl">
      <div className="mb-4">
        <h1 className="text-xl font-semibold">My Work Today</h1>
        <div className="text-sm text-muted-foreground">The engine sorted your day — work top to bottom.</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi label="Call queue" value={counts.call} />
        <Kpi label="Follow-ups due" value={counts.followup} />
        <Kpi label="Mail due" value={counts.mail} />
        <Kpi label="Drafts to approve" value={<Link to="/drafts" className="hover:underline">{drafts}</Link>} accent />
      </div>
      <div className="mb-3 flex gap-1 border-b flex-wrap">
        {tabs.map((t) => (
          <button key={t.k}
            className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-1.5 ${tab === t.k ? "border-amber-500 text-foreground" : "border-transparent text-muted-foreground"}`}
            onClick={() => setTab(t.k)}>
            {t.icon}{t.label}<span className="ml-1 text-xs bg-muted rounded px-1.5">{counts[t.k]}</span>
          </button>
        ))}
      </div>
      {loading ? <p className="text-sm text-muted-foreground p-2">Loading…</p>
        : rows.length === 0 ? <p className="text-sm text-muted-foreground p-2">Nothing due in this queue right now. 🎉</p>
        : <div>{rows.map((r) =>
            tab === "call" ? <CallRow key={r.lead_id} r={r} />
            : tab === "mail" ? <MailRow key={r.lead_id} r={r} onMailed={markMailed} />
            : tab === "react" ? <ReactRow key={r.lead_id} r={r} />
            : <FollowRow key={r.lead_id} r={r} />)}</div>}
    </div>
  );
};

MyWorkPage.path = "/work";
export default MyWorkPage;
